import { Button, Dialog, DialogActions, DialogContent, Grid } from '@mui/material';
import { createColumnHelper } from '@tanstack/react-table';
import { useFlags } from 'launchdarkly-react-client-sdk';
import * as _ from 'lodash';
import type { BaseSyntheticEvent, SyntheticEvent } from 'react';
import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import classSet from 'react-classset';
import { useSelector } from 'react-redux';

import { useProgress } from '@/contexts/ProgressProvider';
import { useWebSocketContext } from '@/contexts/WebSocketProvider';
import { AssetSource, MessageType, Permission, PushNotificationEventType } from '@/enums';
import type { Category, Item, ListItem, Pagination } from '@/models';
import { AccessLevelType } from '@/models/channel';
import AssetDetailsModal from '@/pages/assets/asset-details-modal/AssetDetailsModal';
import { enhanceFetchedAsset, enhanceFetchedAssets } from '@/pages/assets/asset-listing/enhanceFetchedAsset';
import ClipGroupModal from '@/pages/assets/clip-group-modal/ClipGroupModal';
import { FuelPortalService } from '@/services';
import { useAppDispatch } from '@/store';
import { AssetForm } from '@/store/forms';
import type { AssetFilterTypeOptions } from '@/store/forms/Asset-Editor/AssetFilterTypeOptions';
import { AssetFilterType } from '@/store/forms/Asset-Editor/AssetFilterTypeOptions';
import { SchemaType } from '@/store/forms/Asset-Editor/SchemaTypeOptions';
import { applicationDataSelector } from '@/store/slices/application-data';
import { assetEditorSelector, UpdateAssetForm } from '@/store/slices/asset-editor';
import { fuelUserAccessSliceSelector } from '@/store/slices/fuelUserAccess';
import type { StartJobPayloadExtraParams } from '@/store/slices/saveAssetJobsState/SaveAssetJobsState';
import { batchUpload, startJob } from '@/store/slices/saveAssetJobsState/SaveAssetJobsState';
import useAppUtils from '@/utils/appUtils';
import clientUtils from '@/utils/clientUtils';
import { cliptoolUtils } from '@/utils/cliptoolUtils';
import { dateUtils } from '@/utils/dateUtils';

import FuelAutoComplete from '../fuel-autocomplete';
import FuelDialogHeader from '../fuel-dialog-header/fuel-dialog-header';
import FuelSearchInput from '../fuel-search-input';
import FuelTable from '../fuel-table/FuelTable';
import FuelTags from '../fuel-tags';
import FuelAssetThumbnail from '../FuelAssetThumbnail/FuelAssetThumbnail';
import FuelAssetTitle from '../FuelAssetTitle/FuelAssetTitle';

const MAX_FILE_SIZE_10GB = { name: '10 GB limit', fileSize: 10 * 1024 * 1024 * 1024 };
const columnHelper = createColumnHelper();

const initialPagination: Pagination = {
  sortBy: 'publicationDate',
  descending: true,
  rowsPerPage: 10,
  page: 1,
  totalItems: 0,
  pages: 1,
};

export interface SearchState {
  isLoading: boolean;
  showFileUpload: boolean;
  schemaType: string;
  searchText: string;
  selectedCategories: Category[];
  selectedTags: string[];
  extensionOptions: ListItem[];
  selectedExtensions: ListItem[];
  assetTypeOptions: AssetFilterTypeOptions[];
  selectedAssetFilterTypes: AssetFilterTypeOptions[];
  excludeAssetFilterTypes: AssetFilterTypeOptions[];
  pagination: Pagination;
  items: Item[];
  selectedItems: Item[];
  multiple: boolean;
}

const defaultSearchState: SearchState = {
  isLoading: false,
  showFileUpload: false,
  schemaType: '',
  searchText: '',
  selectedCategories: [],
  selectedTags: [],
  extensionOptions: [],
  selectedExtensions: [],
  assetTypeOptions: [],
  selectedAssetFilterTypes: [],
  excludeAssetFilterTypes: [],
  pagination: initialPagination,
  items: [],
  selectedItems: [],
  multiple: false,
};

export interface VNLibraryArgs {
  libraryType: VNLibraryType;
  item: string | Item; // returns 'new' for caller to create new item, else Item object
  items: string[] | Item[]; // a list of items when multiple is true
}

export enum VNLibraryType {
  Video = 'Video',
  Image = 'Image',
  Series = 'Series',
  Season = 'Season',
  Extra = 'Extra',
  Trailer = 'Trailer',
}

interface Iprops {
  libraryType?: VNLibraryType;
  onItemSelected?: (event: BaseSyntheticEvent, value: VNLibraryArgs) => void | null;
  onClose?: (event: BaseSyntheticEvent) => void | null;
  multiple?: boolean;
}

const VNLibrary = ({ libraryType = VNLibraryType.Image, onItemSelected = null, onClose = null, multiple = false }: Iprops) => {
  const { ouData, categories } = useSelector(applicationDataSelector);
  const { permissions } = useSelector(fuelUserAccessSliceSelector);
  const flags = useFlags();
  const { alert } = useAppUtils();
  const dispatch = useAppDispatch();

  const stateRef = useRef<SearchState>(defaultSearchState);

  useEffect(() => {
    // console.log('VNLibrary here', { libraryType, onItemSelected, onClose, multiple, state: _.cloneDeep(stateRef.current) });
    if (multiple === true) {
      stateRef.current.multiple = true;
    }
    initData();
  }, []);

  // ------------------------------------------------------------------
  // Asset Details Modal
  // ------------------------------------------------------------------
  const { assetForm } = useSelector(assetEditorSelector);
  const [openAssetDetailsModal, setOpenAssetDetailsModal] = useState(false);
  const [openClipGroupModal, setOpenClipGroupModal] = useState(false);

  const onTableAction = async (event: BaseSyntheticEvent, type: string, item: Item) => {
    // console.log('onTableAction', { event, type, item });
    if (type === 'edit' && permissions && permissions.has(Permission.EditAsset)) {
      await openEditAssetPopup(event, item);
    }
  };

  const createAssetForm = async (id: string): Promise<AssetForm> => {
    try {
      let asset = await FuelPortalService.getClipById(id);
      asset = enhanceFetchedAsset(asset, permissions, flags, false, [], [], ouData);
      if (asset) {
        const assetFormValue = await AssetForm.createFromData(asset, permissions, ouData);
        dispatch(UpdateAssetForm(assetFormValue));
        return assetFormValue;
      }
      return null;
    } catch (ex) {
      alert('Error fetching asset.', 'error');
      console.error('Error fetching asset', ex);
      return null;
    }
  };

  const openEditAssetPopup = async (event, item: Item) => {
    if (item.id) {
      const form = await createAssetForm(item.id);
      if (form && form.source === 'ClipGroup') {
        setOpenClipGroupModal(true);
      } else {
        setOpenAssetDetailsModal(true);
      }
    }
  };

  const refreshAssets = async (items: Item[]) => {
    setProgress(true);
    setTimeout(() => {
      throttledCallback();
      setProgress(false);
    }, 300);
  };

  // ------------------------------------------------------------------
  // Library Modal
  // ------------------------------------------------------------------
  const [isLibraryOpen, setIsLibraryOpen] = useState(false);
  const [items, setItems] = useState<Item[]>([]);
  const [dropItems, setDropItems] = useState<any>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [clearInput, setClearInput] = useState(0);
  const { loading, setProgress } = useProgress();

  const { lastJsonMessage, readyState } = useWebSocketContext();

  const rowRefs = useRef([]);

  useEffect(() => {
    // console.log('lastJsonMessage', lastJsonMessage);
    if (lastJsonMessage !== null) {
      readWebsocketMessage(lastJsonMessage);
    }
  }, [lastJsonMessage]);

  const readWebsocketMessage = async (message) => {
    // console.log('message received', { message, isLibraryOpen });
    if (
      isLibraryOpen &&
      message &&
      message.messageType === MessageType.Notification &&
      message.data &&
      message.data.eventType === PushNotificationEventType.IngestStatusUpdate
    ) {
      const clipId = message.data.id;
      if (clipId) {
        let asset = await FuelPortalService.getClipById(clipId);
        asset = enhanceFetchedAsset(asset, permissions, flags, false, [], [], ouData);
        const index = items.findIndex((clip) => clip.id === clipId);
        if (index > -1) {
          let forceSetSelectedIndex = -1;
          if (selectedAssets.length > 0) {
            forceSetSelectedIndex = items.findIndex((x) => x.id == selectedAssets[0].id);
          }
          // console.log('thumbnail copy?', { newThumb: asset.thumbnailSrc, old: items[index].thumbnailSrc });
          // carry over special thumbnail
          if (
            (asset.thumbnailSrc == null || asset.thumbnailSrc == '/static/images/thumbnail.png') &&
            items[index].thumbnailSrc != null
          ) {
            if (asset.data.thumbnail == null) {
              asset.data.thumbnail = {};
            }
            asset.data.thumbnail.previewUri = items[index].thumbnailSrc;
            asset.thumbnailSrc = items[index].thumbnailSrc;
          }
          const row = rowRefs.current[clipId];
          const ingestStatusNotCompletedFromExisting = items.find(
            (clip) => clip.id === clipId && clip.data.ingestStatus !== 'Completed'
          );
          if (row && asset && asset.data && asset.data.ingestStatus === 'Completed' && ingestStatusNotCompletedFromExisting) {
            row.classList.add('fuel-table-row--highlight');
            let myItems: Item[] = _.cloneDeep(stateRef.current.items);
            myItems = myItems.map((item) => (item.id === clipId ? asset : item));
            stateRef.current.items = myItems;
            setItems(myItems);
            setTimeout(() => {
              row.classList.remove('fuel-table-row--highlight');
            }, 2000); // Highlight for 2 seconds
          } else {
            let myItems: Item[] = _.cloneDeep(stateRef.current.items);
            myItems = myItems.map((item) => (item.id === clipId ? asset : item));
            stateRef.current.items = myItems;
            setItems(myItems);
          }

          // carry over selection state (sync back to FuelTable)
          if (stateRef.current.multiple !== true && forceSetSelectedIndex > -1) {
            // console.log('forcing selected index', { forceSetSelectedIndex, items: _.cloneDeep(items) });
            setTimeout(() => {
              selectItem(null, items[forceSetSelectedIndex]);
            }, 300);
          }
        }
      }
    }
  };

  const selectItem = (event: SyntheticEvent, item: Item) => {
    const newSelection = [item];
    setForceSelectedAssets(newSelection);
    stateRef.current.selectedItems = newSelection;
    setSelectedAssets(newSelection);
  };

  const getColumns = (columnHelper, libraryType: VNLibraryType) => {
    const videoColumns = [
      columnHelper.accessor('thumbnailSrc', {
        header: () => '',
        width: 75,
        sortable: false,
        cell: (cell) => {
          return (
            <FuelAssetThumbnail
              item={cell.row.original}
              permissions={permissions}
              onClick={(event) => selectItem(event, cell.row.original)}
              hideStatus
            />
          );
        },
      }),
      columnHelper.accessor('title', {
        align: 'left',
        header: () => 'Title',
        width: 170,
        sortable: true,
        cell: (cell) => (
          <FuelAssetTitle
            item={cell.row.original}
            permissions={permissions}
            onClick={(event) => selectItem(event, cell.row.original)}
            show
          />
        ),
      }),
      columnHelper.accessor('extension', {
        align: 'left',
        header: () => 'Type',
        width: 50,
        sortable: false,
        cell: (cell) => {
          let ext = '';
          if (!clientUtils.isNullOrWhitespace(cell?.row?.original?.data?.originalFileName)) {
            const i = cell.row.original.data.originalFileName.indexOf('.');
            if (i > -1) {
              ext = cell.row.original.data.originalFileName.substring(i);
            }
          }
          return <div title={ext}>{ext}</div>;
        },
      }),
      columnHelper.accessor('publicationDate', {
        header: `Upload Date (${ouData.timezoneAbbreviation})`,
        width: 150,
        sortable: true,
        cell: (cell) => dateUtils.format(cell.getValue(), ouData.timezone),
      }),
      columnHelper.accessor('durationString', {
        header: 'Duration',
        width: 75,
        sortable: true,
        cell: (cell) => clientUtils.formatDuration(cell.row.original.duration),
      }),
      columnHelper.accessor('data.accessSettings.effectiveAccessLevel', {
        header: () => `Access`,
        footer: (props) => props.column.id,
        width: 130,
        sortable: false,
        cell: (cell) => cell.row.original.data?.accessSettings?.effectiveAccessLevel || AccessLevelType.Free,
      }),
      columnHelper.accessor('embargoDate', {
        header: () => `Embargo (${ouData.timezoneAbbreviation})`,
        width: 130,
        sortable: true,
        cell: (cell) => dateUtils.format(cell.getValue(), ouData.timezone),
      }),
      columnHelper.accessor('expireDate', {
        header: `Expiry (${ouData.timezoneAbbreviation})`,
        width: 130,
        sortable: true,
        cell: (cell) => dateUtils.format(cell.getValue(), ouData.timezone),
      }),
      columnHelper.accessor('categoryString', {
        header: 'Categories',
        footer: (props) => props.column.id,
        width: 130,
        sortable: true,
        cell: (cell) => <div title={cell.getValue()}>{cell.getValue()}</div>,
      }),
      columnHelper.accessor('tagString', {
        header: 'Tags',
        footer: (props) => props.column.id,
        sortable: true,
        width: 130,
        cell: (cell) => <div title={cell.getValue()}>{cell.getValue()}</div>,
      }),
      // columnHelper.accessor('actions', {
      //   header: 'Actions',
      //   footer: (props) => props.column.id,
      //   width: 75,
      //   sortable: false,
      //   cell: (cell) => (
      //     <FuelActionbar
      //       actions={[
      //         {
      //           title: 'Edit',
      //           className: 'action',
      //           type: 'edit',
      //           iconClass: 'icon material-icons',
      //           iconName: 'mode_edit',
      //           isDisabled: false,
      //         },
      //       ]}
      //       item={cell.row.original}
      //       onActionButtonClick={onTableAction}
      //       context={cell}
      //     />
      //   ),
      // }),
    ];

    const imageColumns = [
      columnHelper.accessor('thumbnailSrc', {
        header: () => '',
        width: 75,
        sortable: false,
        cell: (cell) => {
          return (
            <FuelAssetThumbnail
              item={cell.row.original}
              permissions={permissions}
              onClick={(event) => selectItem(event, cell.row.original)}
              hideStatus
            />
          );
        },
      }),
      columnHelper.accessor('title', {
        align: 'left',
        header: () => 'Title',
        width: 250,
        sortable: true,
        cell: (cell) => (
          <FuelAssetTitle
            item={cell.row.original}
            permissions={permissions}
            onClick={(event) => selectItem(event, cell.row.original)}
          />
        ),
      }),
      columnHelper.accessor('extension', {
        align: 'left',
        header: () => 'Type',
        width: 50,
        sortable: false,
        cell: (cell) => {
          let ext = '';
          if (!clientUtils.isNullOrWhitespace(cell?.row?.original?.data?.originalFileName)) {
            const i = cell.row.original.data.originalFileName.indexOf('.');
            if (i > -1) {
              ext = cell.row.original.data.originalFileName.substring(i);
            }
          }
          return <div title={ext}>{ext}</div>;
        },
      }),
      columnHelper.accessor('publicationDate', {
        header: `Upload Date (${ouData.timezoneAbbreviation})`,
        width: 150,
        sortable: true,
        cell: (cell) => dateUtils.format(cell.getValue(), ouData.timezone),
      }),
      columnHelper.accessor('categoryString', {
        header: 'Categories',
        footer: (props) => props.column.id,
        width: 130,
        sortable: true,
        cell: (cell) => <div title={cell.getValue()}>{cell.getValue()}</div>,
      }),
      columnHelper.accessor('tagString', {
        header: 'Tags',
        footer: (props) => props.column.id,
        sortable: true,
        width: 130,
        cell: (cell) => <div title={cell.getValue()}>{cell.getValue()}</div>,
      }),
      // columnHelper.accessor('actions', {
      //   header: 'Actions',
      //   footer: (props) => props.column.id,
      //   width: 75,
      //   sortable: false,
      //   cell: (cell) => (
      //     <FuelActionbar
      //       actions={[
      //         {
      //           title: 'Edit',
      //           className: 'action',
      //           type: 'edit',
      //           iconClass: 'icon material-icons',
      //           iconName: 'mode_edit',
      //           isDisabled: false,
      //         },
      //       ]}
      //       item={cell.row.original}
      //       onActionButtonClick={onTableAction}
      //       context={cell}
      //     />
      //   ),
      // }),
    ];

    if (libraryType === VNLibraryType.Video) {
      return videoColumns;
    }
    return imageColumns;
  };

  const columns = getColumns(columnHelper, libraryType);

  const getPagination = () => {
    let paginationNew = pagination;
    if (!paginationNew) {
      paginationNew = _.cloneDeep(initialPagination);
      paginationNew.page = 1;
    }
    return paginationNew;
  };

  const paginationChange = (value: Pagination) => {
    // console.log('paginationChange', value);
    stateRef.current.pagination = value;
    setPagination(value);
    throttledCallback();
  };

  const throttledCallback = _.debounce(function () {
    doSearch();
  }, 0);

  const doSearch = async () => {
    // console.log('doSearch', { excludeAssetFilterTypes });
    setProgress(true);
    const myPagination = getPagination();

    // console.log('myPagination', { myPagination });

    let {
      schemaType,
      searchText,
      selectedCategories,
      selectedTags,
      selectedExtensions,
      selectedAssetFilterTypes,
      excludeAssetFilterTypes,
    } = stateRef.current;
    const extensions = selectedExtensions.map((c) => {
      return c.id;
    });
    const categoryNames = selectedCategories.map((c) => {
      return c.name;
    });
    const resolveSortBy = () => {
      switch (myPagination.sortBy) {
        case 'categoryString':
          return 'categories';
        case 'tagString':
          return 'tags';
        case 'durationString':
          return 'duration';
        default:
          return myPagination.sortBy;
      }
    };

    selectedAssetFilterTypes = _.uniqBy(selectedAssetFilterTypes, 'id');

    // #2916 always exclude series and season by default
    // if (!allowSeriesAndSeasons) {
    //   excludeAssetFilterTypes.push({ id: AssetFilterType.Series, name: AssetFilterType.Series });
    //   excludeAssetFilterTypes.push({ id: AssetFilterType.Season, name: AssetFilterType.Season });
    // }
    excludeAssetFilterTypes = _.uniqBy(excludeAssetFilterTypes, 'id');

    setClearSelectedRow(true);
    const results = await FuelPortalService.searchAsset(
      searchText,
      ouData.ouId,
      myPagination.page - 1,
      myPagination.rowsPerPage,
      categoryNames,
      selectedTags,
      resolveSortBy(),
      myPagination.descending ? 'desc' : 'asc',
      true,
      'clip',
      schemaType,
      false,
      selectedAssetFilterTypes,
      excludeAssetFilterTypes,
      null,
      null,
      null,
      null,
      null,
      null,
      null,
      null,
      false,
      extensions
    );
    const paginationObj = myPagination;
    if (results && results.data) {
      const assets = enhanceFetchedAssets(results.data, permissions, flags, true, [], [], ouData);

      paginationObj.totalItems = results.pagination.total;
      paginationObj.pages = Math.ceil(paginationObj.totalItems / paginationObj.rowsPerPage);
      stateRef.current.pagination = paginationObj;
      setPagination(paginationObj);
      stateRef.current.items = assets;
      setItems(assets);
    } else {
      paginationObj.totalItems = 0;
      paginationObj.pages = 0;
      stateRef.current.pagination = paginationObj;
      setPagination(paginationObj);
      stateRef.current.items = [];
      setItems([]);
    }
    setProgress(false);
  };

  // ------------------------------------------------------------------
  // Region: Search Control
  // ------------------------------------------------------------------

  const onSearchTextChanged = (value) => {
    // console.log('onSearchTextChanged', { value });
    stateRef.current.searchText = value;
  };

  const onSearchTextClick = (value) => {
    // console.log('onSearchTextClick', { value });
    if (value != null) {
      stateRef.current.searchText = value;
    }
    onSearchClick();
  };

  const resetPagination = (value = null) => {
    // console.log('onSearchClick', { value });
    const paginationObject = pagination;
    if (paginationObject != null) {
      paginationObject.page = 1;
      paginationObject.totalItems = 0;
      paginationObject.pages = 1;
    }
  };

  const getFreshState = () => {
    const defaults: SearchState = _.cloneDeep(defaultSearchState);
    const newState: SearchState = _.cloneDeep(stateRef.current);

    newState.schemaType = getSchemaType(libraryType);
    newState.assetTypeOptions = defaults.assetTypeOptions;
    newState.searchText = defaults.searchText;
    newState.selectedCategories = defaults.selectedCategories;
    newState.selectedTags = defaults.selectedTags;
    newState.selectedExtensions = defaults.selectedExtensions;

    return newState;
  };

  const resetSearchState = () => {
    setClearInput(clearInput + 1);
    const newState = getFreshState();
    stateRef.current = newState;
  };

  const getSchemaType = (libraryType: VNLibraryType) => {
    if (libraryType == VNLibraryType.Image) {
      return SchemaType.Image;
    }
    return SchemaType.Video;
  };

  const onResetClick = (value = null) => {
    // console.log('onSearchClick', { value });
    resetSearchState();
    resetPagination();
    throttledCallback();
  };

  const onSearchClick = (value = null) => {
    // console.log('onSearchClick', { value });
    resetPagination();
    throttledCallback();
  };

  const onTagsChanged = (value) => {
    // console.log('onTagsChanged');
    stateRef.current.selectedTags = value;
    // setSelectedTags(value);
    // throttledCallback();
  };

  const onCategoriesChanged = (value) => {
    stateRef.current.selectedCategories = value;
    // setSelectedCategories(value);
    // throttledCallback();
  };

  // ------------------------------------------------------------------
  // Region: Extension Dropdown
  // ------------------------------------------------------------------

  const onExtensionsChanged = (val) => {
    // console.log('onExtensionsChanged', { val });
    stateRef.current.selectedExtensions = val;
  };

  // ------------------------------------------------------------------
  // Region: Asset Types Dropdown
  // ------------------------------------------------------------------

  const getAssetFilterTypeOptions = () => {
    return stateRef.current.assetTypeOptions;
  };

  const onAssetFilterTypesChanged = (val) => {
    // console.log('onAssetFilterTypesChanged', { val });
    stateRef.current.selectedAssetFilterTypes = val;
  };

  // ------------------------------------------------------------------
  // Region: Base Component Button Actions
  // ------------------------------------------------------------------

  const initData = () => {
    // console.log('initData', stateRef.current.isLoading);

    if (stateRef.current.isLoading === false || (stateRef.current.isLoading === true && stateRef.current.schemaType == '')) {
      try {
        // console.log('loading data', { libraryType, isLoading: stateRef.current.isLoading });
        stateRef.current.isLoading = true;
        resetPagination();
        const newState = getFreshState();
        newState.isLoading = true;
        if (libraryType == VNLibraryType.Trailer) {
          newState.extensionOptions = [];
          newState.selectedAssetFilterTypes = [
            // { id: AssetFilterType.NestedChannel, name: 'Nested/Collections' },
            // { id: AssetFilterType.ClipGroup, name: 'Clip Groups' },
            // { id: AssetFilterType.LiveProgramAssets, name: 'Live Program Assets' },
            // { id: AssetFilterType.VODFileAssets, name: 'VOD File Assets' },
            // { id: AssetFilterType.Transitions, name: 'Transitions' },
            // { id: AssetFilterType.Image, name: 'Images' },
            // { id: AssetFilterType.Collection, name: AssetFilterType.Collection },
            // { id: AssetFilterType.Series, name: AssetFilterType.Series },
            // { id: AssetFilterType.Season, name: AssetFilterType.Season },
            // { id: AssetFilterType.Extra, name: AssetFilterType.Extra },
            { id: AssetFilterType.Trailer, name: AssetFilterType.Trailer },
          ];
          newState.excludeAssetFilterTypes = [];
        } else if (libraryType == VNLibraryType.Extra) {
          newState.extensionOptions = [];
          newState.selectedAssetFilterTypes = [
            // { id: AssetFilterType.NestedChannel, name: 'Nested/Collections' },
            // { id: AssetFilterType.ClipGroup, name: 'Clip Groups' },
            // { id: AssetFilterType.LiveProgramAssets, name: 'Live Program Assets' },
            // { id: AssetFilterType.VODFileAssets, name: 'VOD File Assets' },
            // { id: AssetFilterType.Transitions, name: 'Transitions' },
            // { id: AssetFilterType.Image, name: 'Images' },
            // { id: AssetFilterType.Collection, name: AssetFilterType.Collection },
            // { id: AssetFilterType.Series, name: AssetFilterType.Series },
            // { id: AssetFilterType.Season, name: AssetFilterType.Season },
            { id: AssetFilterType.Extra, name: AssetFilterType.Extra },
            // { id: AssetFilterType.Trailer, name: AssetFilterType.Trailer },
          ];
          newState.excludeAssetFilterTypes = [];
        } else if (libraryType == VNLibraryType.Series) {
          newState.extensionOptions = [];
          newState.selectedAssetFilterTypes = [
            // { id: AssetFilterType.NestedChannel, name: 'Nested/Collections' },
            // { id: AssetFilterType.ClipGroup, name: 'Clip Groups' },
            // { id: AssetFilterType.LiveProgramAssets, name: 'Live Program Assets' },
            // { id: AssetFilterType.VODFileAssets, name: 'VOD File Assets' },
            // { id: AssetFilterType.Transitions, name: 'Transitions' },
            // { id: AssetFilterType.Image, name: 'Images' },
            // { id: AssetFilterType.Collection, name: AssetFilterType.Collection },
            { id: AssetFilterType.Series, name: AssetFilterType.Series },
            // { id: AssetFilterType.Season, name: AssetFilterType.Season },
            // { id: AssetFilterType.Extra, name: AssetFilterType.Extra },
            // { id: AssetFilterType.Trailer, name: AssetFilterType.Trailer },
          ];
          newState.excludeAssetFilterTypes = [];
        } else if (libraryType == VNLibraryType.Season) {
          newState.extensionOptions = [];
          newState.selectedAssetFilterTypes = [
            // { id: AssetFilterType.NestedChannel, name: 'Nested/Collections' },
            // { id: AssetFilterType.ClipGroup, name: 'Clip Groups' },
            // { id: AssetFilterType.LiveProgramAssets, name: 'Live Program Assets' },
            // { id: AssetFilterType.VODFileAssets, name: 'VOD File Assets' },
            // { id: AssetFilterType.Transitions, name: 'Transitions' },
            // { id: AssetFilterType.Image, name: 'Images' },
            // { id: AssetFilterType.Collection, name: AssetFilterType.Collection },
            // { id: AssetFilterType.Series, name: AssetFilterType.Series },
            { id: AssetFilterType.Season, name: AssetFilterType.Season },
            // { id: AssetFilterType.Extra, name: AssetFilterType.Extra },
            // { id: AssetFilterType.Trailer, name: AssetFilterType.Trailer },
          ];
          newState.excludeAssetFilterTypes = [];
        } else if (libraryType == VNLibraryType.Video) {
          newState.showFileUpload = true;
          newState.extensionOptions = [
            { id: 'mov', name: '.mov' },
            { id: 'mpeg4', name: '.mpeg4' },
            { id: 'mxf', name: '.mxf' },
            { id: 'wmv', name: '.wmv' },
            { id: 'mp4', name: '.mp4' },
            { id: 'mpeg', name: '.mpeg' },
            { id: 'mpg', name: '.mpg' },
            { id: 'mkv', name: '.mkv' },
          ];
          newState.selectedAssetFilterTypes = [
            { id: AssetFilterType.NestedChannel, name: 'Nested/Collections' },
            { id: AssetFilterType.ClipGroup, name: 'Clip Groups' },
            { id: AssetFilterType.LiveProgramAssets, name: 'Live Program Assets' },
            { id: AssetFilterType.VODFileAssets, name: 'VOD File Assets' },
            // { id: AssetFilterType.Transitions, name: 'Transitions' },
            // { id: AssetFilterType.Image, name: 'Images' },
            // { id: AssetFilterType.Collection, name: AssetFilterType.Collection },
            // { id: AssetFilterType.Series, name: AssetFilterType.Series },
            // { id: AssetFilterType.Season, name: AssetFilterType.Season },
            // { id: AssetFilterType.Extra, name: AssetFilterType.Extra },
            // { id: AssetFilterType.Trailer, name: AssetFilterType.Trailer },
          ];
          newState.excludeAssetFilterTypes = [{ id: AssetFilterType.Collection, name: AssetFilterType.Collection }];
        } else if (libraryType == VNLibraryType.Image) {
          newState.showFileUpload = true;
          newState.extensionOptions = [
            { id: 'jpeg', name: '.jpeg' },
            { id: 'jpg', name: '.jpg' },
            { id: 'png', name: '.png' },
            { id: 'gif', name: '.gif' },
            { id: 'svg', name: '.svg' },
          ];
          newState.selectedAssetFilterTypes = [
            // { id: AssetFilterType.NestedChannel, name: 'Nested/Collections' },
            // { id: AssetFilterType.ClipGroup, name: 'Clip Groups' },
            // { id: AssetFilterType.LiveProgramAssets, name: 'Live Program Assets' },
            // { id: AssetFilterType.VODFileAssets, name: 'VOD File Assets' },
            // { id: AssetFilterType.Transitions, name: 'Transitions' },
            { id: AssetFilterType.Image, name: 'Images' },
            // { id: AssetFilterType.Series, name: AssetFilterType.Series },
            // { id: AssetFilterType.Season, name: AssetFilterType.Season },
            // { id: AssetFilterType.Extra, name: AssetFilterType.Extra },
            // { id: AssetFilterType.Trailer, name: AssetFilterType.Trailer },
          ];
          newState.excludeAssetFilterTypes = [];
        }
        stateRef.current = newState;

        throttledCallback();
      } finally {
        setTimeout(() => {
          // console.log('reseting loading flag');
          stateRef.current.isLoading = false;
        }, 0);
      }
    }
  };

  const handleLibraryClose = (event: BaseSyntheticEvent) => {
    // console.log('handleLibraryClose');
    onClose?.(event);
  };

  const createNewCollection = (event: BaseSyntheticEvent) => {
    // console.log('createNewCollection');

    onItemSelected?.(event, { libraryType, item: 'new' });
  };

  // ------------------------------------------------------------------
  // Table Item Selection
  // ------------------------------------------------------------------
  const [clearSelectedRow, setClearSelectedRow] = useState(false);
  const [selectedAssets, setSelectedAssets] = useState<Item[]>([]);
  const [forceSelectedAssets, setForceSelectedAssets] = useState<Item[]>([]);

  const handleSelectedRows = (items: Item[]) => {
    // console.log('handleSelectedRows', { items });
    stateRef.current.selectedItems = items;
    setSelectedAssets(items);
  };

  const handleItemSelected = (event: BaseSyntheticEvent) => {
    // console.log('handleItemSelected', { selectedAssets });
    if (selectedAssets.length > 0) {
      onItemSelected?.(event, { libraryType, item: selectedAssets[0], items: selectedAssets });
      // setValue(`tmp.itemLookup.${selectedAssets[0].id}`, selectedAssets[0]);
      onClose?.(event);
    }
  };

  // ------------------------------------------------------------------
  // Drag and Drop Files
  // ------------------------------------------------------------------
  const fileInput = useRef<HTMLInputElement | null>(null);
  const [isDragOverActive, setDragOverActive] = useState(false);
  // const [ingestFile, setIngestFile] = useState(null);
  // const [ingestFiles, setIngestFiles] = useState<FileList>([]);
  // const [isMultiple, setIsMultiple] = useState(false);

  const onBrowseButtonClick = (event: BaseSyntheticEvent) => {
    event.stopPropagation();
    // `current` points to the mounted file input element
    if (fileInput && fileInput.current) {
      fileInput.current.click();
    }
  };

  const onDragEnter = (event: React.DragEvent<HTMLDivElement>) => {
    // console.log('onDragEnter', { event: _.cloneDeep(event) });
    event.preventDefault(); // NOTE: this is required otherwise drag and drop won't work!
    event.stopPropagation();
    setDragOverActive(true);
  };
  const onDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    // console.log('onDragOver', { event: _.cloneDeep(event) });
    event.preventDefault(); // NOTE: this is required otherwise drag and drop won't work!
    event.stopPropagation();
    setDragOverActive(true);
  };

  const onDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    // console.log('onDragLeave', { event: _.cloneDeep(event) });
    event.preventDefault(); // NOTE: this is required otherwise drag and drop won't work!
    event.stopPropagation();
    setDragOverActive(false);
  };
  const onDragEnd = (event: React.DragEvent<HTMLDivElement>) => {
    // console.log('onDragEnd', { event: _.cloneDeep(event) });
    event.preventDefault(); // NOTE: this is required otherwise drag and drop won't work!
    event.stopPropagation();
    setDragOverActive(false);
  };
  const onFileDrop = (event: React.DragEvent<HTMLDivElement>, info) => {
    // console.log('onFileDrop', { event: _.cloneDeep(event), info });
    event.preventDefault(); // NOTE: this is required otherwise drag and drop won't work!
    event.stopPropagation();
    setDragOverActive(false);
    const { files } = event.dataTransfer;
    processFileChange(files);
  };

  const getAssetTitle = (filename: string) => {
    return filename.replace('-', ' ').replace('_', ' ');
  };

  const getAssetForm = (form: AssetForm, file: File) => {
    const newAssetForm: AssetForm = _.cloneDeep(form);
    const fileName = getAssetTitle(file.name);
    newAssetForm.title = fileName;
    newAssetForm.originalFileName = file.name;
    newAssetForm.ingestFile = file;
    return newAssetForm;
  };

  const getImgBlobsAsync = async (fileList: FileList) => {
    // console.log('getImgBlobsAsync', { fileList });
    const promises = [];
    for (let i = 0; i < fileList.length; i += 1) {
      const file = fileList[i];
      const ext = clientUtils.getExtension(file.name);

      // console.log('getImgBlobsAsync file', { file, ext });
      if (libraryType === VNLibraryType.Image) {
        promises.push(cliptoolUtils.getFileImageBlob(file));
      } else if (ext === '.mxf' || ext === '.mpg' || ext === '.mov') {
        promises.push(new Promise<string>((resolve, reject) => resolve(null)));
      } else {
        promises.push(cliptoolUtils.getVideoThumbnail(file));
      }
    }

    // Now that all the asynchronous operations are running, here we wait until they all complete.
    const results = await Promise.all(promises);
    return results;
  };

  const processFileChange = async (fileList: FileList) => {
    // console.log('processFileChange', { fileList });
    if (fileList == null || fileList.length <= 0) {
      alert(`No file selected.  Please try again.`, 'warning');
      return;
    }
    if (stateRef.current.multiple !== true && fileList.length > 1) {
      alert(`Multiple files not allowed.  Please select only one file.`, 'warning');
      return;
    }

    const schemaType = stateRef.current.schemaType === 'Video' ? SchemaType.Video : SchemaType.Image;

    const files: File[] = [];
    for (let i = 0; i < fileList.length; i += 1) {
      // validate file extension
      const warningMessage = clientUtils.validateFileExtension(schemaType, fileList[i].name);
      if (warningMessage != null) {
        alert(warningMessage, 'warning');
        return;
      }

      // validate file size
      const file: File = fileList[0];
      const maxFileSize = MAX_FILE_SIZE_10GB;
      const fileSize = file.size;
      if (fileSize >= maxFileSize.fileSize) {
        alert(
          `File size is restricted to ${maxFileSize.name}.  ${file.name} is ${Math.floor(
            fileSize / 1000000000
          )} GB, please consider breaking it out.`
        );
        return;
      }

      files.push(fileList[i]);
    }

    // setIngestFiles(fileList);

    // setLoading(true);
    try {
      const imgBlobs: string[] = await getImgBlobsAsync(fileList);

      // console.log('imgBlobs', { imgBlobs });

      const myAssetForm = await AssetForm.create(permissions, ouData);
      myAssetForm.originalFileName = '';
      myAssetForm.schemaType = schemaType;
      myAssetForm.ingestFile = null;
      myAssetForm.ingestFiles = files;
      myAssetForm.title = '';
      myAssetForm.source = AssetSource.Ingest;

      const extraParams: StartJobPayloadExtraParams = {
        returnItemCallback: (item: Item, index) => {
          // console.log('returnItemCallback', { item, index });
          if (item == null) {
            return;
          }
          const enhancedItem = enhanceFetchedAsset(item, permissions, flags, true, [], [], ouData);
          if (imgBlobs.length > index && imgBlobs[index] != null) {
            const imgBlob = imgBlobs[index];
            if (enhancedItem.data.thumbnail == null) {
              enhancedItem.data.thumbnail = {};
            }
            if (imgBlob == null) {
              enhancedItem.thumbnailSrc = '/static/images/thumbnail.png';
            } else {
              enhancedItem.data.thumbnail.tempThumbnailBlob = imgBlob;
              enhancedItem.thumbnailSrc = imgBlob;
            }
          }

          const myItems: Item[] = _.cloneDeep(stateRef.current.items);
          const mySelectedItems: Item[] = _.cloneDeep(stateRef.current.selectedItems);
          const myPagination = _.cloneDeep(stateRef.current.pagination);
          myItems.unshift(enhancedItem);
          mySelectedItems.unshift(enhancedItem);
          stateRef.current.selectedItems = mySelectedItems;

          // myItems.pop(); // remove last item so there are still 10 items in the current page
          stateRef.current.items = myItems;
          setItems(myItems);
          myPagination.rowsPerPage += 1;
          myPagination.totalItems += 1; // update total countF
          // myPagination.pages = Math.ceil(myPagination.totalItems / myPagination.rowsPerPage);

          stateRef.current.pagination = myPagination;
          setPagination(myPagination);

          if (!stateRef.current.multiple) {
            setTimeout(() => {
              setForceSelectedAssets([myItems[0]]); // so the FuelTable updates
              stateRef.current.selectedItems = [myItems[0]];
              setSelectedAssets([myItems[0]]);
            }, 300);
          } else {
            setTimeout(() => {
              setForceSelectedAssets(mySelectedItems); // so the FuelTable updates
              setSelectedAssets(mySelectedItems);
            }, index * 100);
          }
        },
      };

      if (files.length === 1) {
        // console.log('single file upload');
        // Single file upload
        const file: File = files[0];
        const frozenForm = getAssetForm(myAssetForm, file);
        // console.log('Automatically upload image with filename.');
        // console.log('imgBlob', { imgBlob });

        // console.log('frozenForm', { frozenForm });

        const startJobResult = await dispatch(startJob({ form: frozenForm, jobType: '', extraParams }));

        if (startJobResult?.payload?.error?.message != null) {
          console.error(startJobResult?.payload?.error?.message);
        }

        // console.log('startJobResult', { startJobResult });
        if (startJobResult?.payload?.payload !== true) {
          alert('There was an error uploading your asset.  Please try again and contact support if the issue persists.', 'error');
        }
      } else {
        // multifile upload
        const frozenForm = _.cloneDeep(myAssetForm);
        // console.log('multifile upload', { frozenForm, files });
        const newAssetForms: AssetForm[] = [];
        for (let i = 0; i < files.length; i += 1) {
          newAssetForms.push(getAssetForm(frozenForm, files[i]));
        }
        // console.log('multifile upload', { newAssetForms });

        const response = await dispatch(batchUpload({ forms: newAssetForms, jobType: 'BatchUpload', extraParams }));
        if (response && response.payload) {
          const assetsSavedSuccessfully = response.payload.filter((r) => r.status === 'fulfilled');
          const errorCount = response.payload.filter((r) => r.status !== 'fulfilled');
          if (errorCount > 0) {
            alert(
              `Not all assets created - ${assetsSavedSuccessfully.length} created successfully, ${errorCount} failed.`,
              'error'
            );
          } else {
            alert(`All ${assetsSavedSuccessfully.length} Assets created successfully.`);
          }
        }
      }
    } catch (ex) {
      console.error({ ex });
    }
    // finally {
    //   setTimeout(() => {
    //     setLoading(false);
    //   }, 0);
    // }

    // only supporting single file upload...multiple files are possible with the drag and drop, so just take the first file...
    // if (fileList.length === 1) {
    //   setIsMultiple(false);
    // }
    // filesChange(fileList);
    if (fileInput && fileInput.current) fileInput.current.value = '';
  };

  // ------------------------------------------------------------------
  // Ingest New Asset Modal (only for video)
  // ------------------------------------------------------------------
  // const [openAssetIngestDialg, setOpenAssetIngestDialg] = useState(false);

  return (
    <div className="vn-library">
      <Dialog
        closeAfterTransition={false}
        className="viewnexa vn-library-modal"
        open
        fullWidth
        maxWidth="xl"
        onClose={handleLibraryClose}
      >
        <FuelDialogHeader onClose={handleLibraryClose}>{`Select ${libraryType} Asset`.toUpperCase()}</FuelDialogHeader>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid className="search-area" item xs={2}>
              <Grid item xs={12}>
                <FuelSearchInput
                  defaultValue={stateRef.current.searchText}
                  onSearch={onSearchTextClick}
                  onChange={onSearchTextChanged}
                  clearInput={clearInput}
                />
              </Grid>
              <Grid item xs={12}>
                <FuelTags
                  id="fuel-ingest-tag-id"
                  tags={stateRef.current.selectedTags}
                  inputTagWidth={365}
                  onChange={onTagsChanged}
                />
              </Grid>
              <Grid item xs={12}>
                <FuelAutoComplete
                  id="category-select"
                  items={categories}
                  itemText="name"
                  itemKey="id"
                  labelText="Categories"
                  placeHolderText="Select Categories"
                  controlWidth={365}
                  multiSelect
                  labelPaddingTop="-5px"
                  defaultValue={stateRef.current.selectedCategories}
                  onChange={onCategoriesChanged}
                />
              </Grid>
              {stateRef.current.extensionOptions.length > 0 && (
                <Grid item xs={12}>
                  <FuelAutoComplete
                    id="extensions-select"
                    items={stateRef.current.extensionOptions}
                    itemText="name"
                    itemKey="id"
                    labelText="Extension"
                    placeHolderText="Select"
                    multiSelect
                    defaultValue={stateRef.current.selectedExtensions}
                    onChange={onExtensionsChanged}
                  />
                </Grid>
              )}
              {/* <Grid item xs={12}>
                  <FuelAutoComplete
                    id="asset-type-select"
                    items={getAssetFilterTypeOptions()}
                    itemText="name"
                    itemKey="id"
                    labelText="Asset Types"
                    placeHolderText="Select Asset Type"
                    multiSelect={true}
                    defaultValue={stateRef.current.selectedAssetFilterTypes}
                    onChange={onAssetFilterTypesChanged}
                  />
                </Grid> */}
              <Grid item xs={12} style={{ textAlign: 'right' }}>
                <Button style={{ margin: '0px 8px' }} variant="contained" onClick={onResetClick}>
                  Reset
                </Button>
                <Button color="success" variant="contained" onClick={onSearchClick}>
                  Search
                </Button>
              </Grid>

              {stateRef.current.showFileUpload && (
                <Grid item xs={12}>
                  <div
                    id="ingest-widget"
                    className={classSet({ 'drag-over-active': isDragOverActive })}
                    onDragOver={onDragOver}
                    onDragEnter={onDragEnter}
                    onDragLeave={onDragLeave}
                    onDragEnd={onDragEnd}
                    onDrop={onFileDrop}
                    test-id="ingest-widget"
                    onClick={onBrowseButtonClick}
                  >
                    <form encType="multipart/form-data">
                      <div className="ingest-drop">
                        {/* <span className="fa fa-cloud-upload"></span> */}
                        <i className="icon material-icons">backup</i>
                        <p>Click to upload or drag and drop</p>
                        <p>(Max File Size 10GB)</p>
                      </div>
                      {/* <p>or</p>
                      <p>Choose file to upload</p> */}
                      <div className="file-section">
                        <input
                          className="file-input"
                          type="file"
                          id="file"
                          ref={fileInput}
                          onChange={(e) => processFileChange(e.target.files)}
                          multiple={stateRef.current.multiple}
                          // multiple
                        />
                        <Button
                          variant="contained"
                          style={{
                            // backgroundColor: '#0db080',
                            fontSize: '13px',
                            marginTop: '5px',
                          }}
                          size="small"
                          className="browse-button"
                          color="success"
                          onClick={onBrowseButtonClick}
                        >
                          BROWSE
                        </Button>
                      </div>
                      {/* {ingestFiles.length === 1 && <label className="file-name">{clientUtils.shortenFileName(ingestFiles[0].name, 32)}</label>} */}
                      {/* {ingestFiles.length > 0 && (
                        <a className="file-link" onClick={openDialog} style={{ cursor: 'pointer' }}>
                          {ingestFiles.length} Files Selected
                        </a>
                      )} */}
                      {/* {myAssetForm.errors && (
                        <div className="text-danger" test-id="ingest-file-error-message">
                          {myAssetForm.errors.message('ingestFile')}
                        </div>
                      )} */}
                    </form>
                  </div>
                </Grid>
              )}
            </Grid>
            <Grid item xs={10}>
              <div className="library-table-container">
                <FuelTable
                  tableHeight={582}
                  columnHelper={columnHelper}
                  data={items}
                  defaultColumns={columns}
                  paginationDetail={pagination}
                  paginationChange={paginationChange}
                  showRowSelection
                  handleSelectedRows={handleSelectedRows}
                  singleSelect={stateRef.current.multiple !== true}
                  clearSelectedRow={clearSelectedRow}
                  loading={loading}
                  pull={false}
                  id="vn-asset-library-table"
                  forceSelectedRows={forceSelectedAssets}
                  rowRefs={rowRefs}
                />
              </div>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          {(libraryType == VNLibraryType.Extra ||
            libraryType == VNLibraryType.Season ||
            libraryType == VNLibraryType.Trailer) && (
            <Button color="success" variant="contained" onClick={createNewCollection}>
              {`Create New ${libraryType}`}
            </Button>
          )}
          <Button variant="contained" onClick={handleLibraryClose}>
            Cancel
          </Button>
          <Button disabled={selectedAssets.length == 0} color="success" variant="contained" onClick={handleItemSelected}>
            Select
          </Button>
        </DialogActions>
      </Dialog>
      {openAssetDetailsModal && (
        <AssetDetailsModal
          open={openAssetDetailsModal}
          onClose={() => setOpenAssetDetailsModal(false)}
          processAssetsReturnedFromSaving={refreshAssets}
        />
      )}
      {openClipGroupModal && (
        <ClipGroupModal
          open={openClipGroupModal}
          onClose={() => setOpenClipGroupModal(false)}
          processAssetsReturnedFromSaving={refreshAssets}
          assetForm={assetForm}
        />
      )}
    </div>
  );
};

export default VNLibrary;
